import type { CustodianConnectionAccountTypeSettings } from '@interfaces/custodianConnection';
import type { CurrencyCodes } from '@onevesthq/ov-enums';

type AccountCurrencySettings = Pick<
  CustodianConnectionAccountTypeSettings,
  'baseCurrency' | 'availableCurrencies' | 'isMultiCurrencyEnabled'
>;

/**
 * The custodian's `availableCurrencies` is the list of currencies it *supports*, which is
 * not the same as the currencies enabled *on the account*. Enabling more than the base
 * currency turns the account into a multi-currency account, so extra currencies are only
 * kept when the multi-currency selector is actually available to the user.
 */
export const resolveAccountCurrencies = ({
  accountTypeSettings,
  currentBaseCurrency,
  currentAvailableCurrencies,
  multiCurrencySelectorEnabled,
}: {
  accountTypeSettings: AccountCurrencySettings;
  currentBaseCurrency?: CurrencyCodes | null;
  currentAvailableCurrencies?: CurrencyCodes[] | null;
  multiCurrencySelectorEnabled: boolean;
}): { baseCurrency: CurrencyCodes; availableCurrencies: CurrencyCodes[] } => {
  const { availableCurrencies, baseCurrency, isMultiCurrencyEnabled } =
    accountTypeSettings;

  const resolvedBaseCurrency =
    currentBaseCurrency && availableCurrencies.includes(currentBaseCurrency)
      ? currentBaseCurrency
      : baseCurrency;

  const canHoldMultipleCurrencies =
    multiCurrencySelectorEnabled && isMultiCurrencyEnabled;

  const additionalCurrencies = canHoldMultipleCurrencies
    ? (currentAvailableCurrencies ?? []).filter(
        (currency) =>
          currency !== resolvedBaseCurrency &&
          availableCurrencies.includes(currency),
      )
    : [];

  return {
    baseCurrency: resolvedBaseCurrency,
    availableCurrencies: [resolvedBaseCurrency, ...additionalCurrencies],
  };
};
